import {abTestingReducer, IABTestingStore} from "@pg-mono/ab-testing";
import {IFavouriteStore} from "@pg-mono/favourites";
import {IResponseStateStore, responseStateReducer} from "@pg-mono/response-state";

import {ILeadFormState, leadFormReducer} from "../application/reducers/lead_form_reducer";
import {ISentLeadState, sentLeadReducer} from "../application/reducers/sent_lead_reducer";
import {mainLeadSlice} from "../application/state/main_lead_slice";
import {tinderMultiLeadSlice} from "../application/state/tinder_multi_lead_slice";
import {MainLeadState} from "../application/types/lead/MainLeadState";
import {ITinderMultiLeadState} from "../application/types/tinder_multi_lead/ITinderMultiLeadState";
import {IMarketplaceStore, marketplaceReducer} from "../architect_offers/reducers/marketplace_reducer";
import {articleReducer, IArticleStore} from "../article/reducers/article_reducer";
import {creditCalculatorReducer, IArticleListStore} from "../credit_calculator/reducers/credit_calculator_reducer";
import {faqReducer, IFaqStore} from "../faq/reducers/faq_reducer";
import {favouriteReducer} from "../favorite/reducers/favourite_reducer";
import {forYouModalSlice} from "../for_you/state/for_you_modal_slice";
import {IForYouModal} from "../for_you/types/IForYouModal";
import {homepageReducer, IHomepageStore} from "../homepage/reducers/homepage_reducer";
import {IInvestmentOffersStore, investmentOffersReducer} from "../investment_offer/reducers/investment_offer_reducer";
import {IMapsStore, mapsReducer} from "../maps/reducers/maps_reducer";
import {IPendingSinglePoiState, pendingSinglePoiSlice} from "../maps/state/pending_single_poi_slice";
import {IMetaDataStore, metaDataReducer} from "../meta_data/reducers/meta_data_reducer";
import {backToUrlReducer, IBackToUrlStore} from "../navigation/reducers/back_to_url_reducer";
import {footerReducer, IFooterStore} from "../navigation/reducers/footer_reducer";
import {INavigationStore, navigationSlice} from "../navigation/state/navigation_slice";
import {INewsletterStore, newsletterReducer} from "../newsletter/reducers/newsletter_reducer";
import {INewsletterSubscriptionModalStore, newsletterSubscriptionModalSlice} from "../newsletter/slice/newsletter_subscription_modal_slice";
import {IOfferDetailStore, offerDetailReducer} from "../offer/detail/reducers/offer_detail_reducer";
import {IOffersNearbyStore, offersNearbyReducer} from "../offer/detail/reducers/offers_nearby_reducer";
import {INotificationsSubscriptionModalStore, notificationsSubscriptionModalReducer} from "../offer/list/reducers/notifications_subscription_modal_reducer";
import {IOfferListStore, offerListReducer} from "../offer/list/reducers/offer_list_reducer";
import {IOfferListMapStore, offerListMapReducer} from "../offer/map/reducers/map_reducer";
import {IOfferListingStore, offerListingSlice} from "../offer/state/offer_listing_slice";
import {ISeenOffersStore, seenOffersSlice} from "../offer/state/seen_offers_slice";
import {IOurOfferStore, ourOfferReducer} from "../our_offer/reducers/our_offer_reducer";
import {IPartnersListStore, partnersListReducer} from "../partners/reducers/partners_list_reducer";
import {IPricesUnlockedState, pricesUnlockedSlice} from "../prices/state/prices_unlocked_slice";
import {IPromotionListListStore, promotionListListReducer} from "../promotion/reducers/promotion_list_reducer";
import {IPropertyDetailStore, propertyDetailReducer} from "../property/reducers/property_reducer";
import {IPropertyConfiguratorStore, propertyConfiguratorStore} from "../property_configurator/reducers/property_configurator_reducer";
import {ISubscribeRealEstateStore, subscribedRealEstatesSlice} from "../real_estate/state/subscribe_real_estate_modal_slice";
import {IReportStore, reportReducer} from "../report/price/reducers/report_reducer";
import {ISearchStore, searchReducer} from "../search/reducers/search_reducer";
import {ISemanticSearchAuthStore, semanticSearchAuthSlice} from "../semantic_search/state/semantic_search_auth_slice";
import {ISiteMapStore, siteMapReducer} from "../site_map/reducers/site_map_reducer";
import {ISmartAdsStore, smartAdsReducer} from "../smart_ad_server/reducer/smart_ads_reducer";
import {googleTagManagerReducer, IGoogleTagManagerStore} from "../tracking/google_tag_manager/reducers/google_tag_manager_reducer";
import {IUiStore, uiReducer} from "../ui/reducers/ui_reducer";
import {IUserStore, userReducer} from "../user/reducers/user_reducer";
import {IVariablesStore, variablesReducer} from "../variables/reducers/variables_reducer";
import {IVendorStore, vendorReducer} from "../vendor/reducers/vendor_reducer";
import {IViewTypeStore, viewTypeReducer} from "../view_type/view_type_reducer";
import {globalsReducer, IGlobalsStore} from "./reducers/globals_reducer";
import {renderVariantsSlice, RenderVariantsState} from "./render_variants/render_variants_slice";

// IMPORTANT! For new slices that use Redux Toolkit, always add them to rootReducerConfig!
// The goal is to eventually eliminate the separate state type and rely only on the type inferred from the reducer configuration!
interface IToolkitStore {
    // Reducers from Redux Toolkit slices - please keep alphabetical order
    forYouModal: IForYouModal;
    mainLead: MainLeadState;
    navigation: INavigationStore;
    newsletterSubscriptionModal: INewsletterSubscriptionModalStore;
    offerListing: IOfferListingStore;
    pendingSinglePoi: IPendingSinglePoiState;
    pricesUnlocked: IPricesUnlockedState;
    renderVariants: RenderVariantsState;
    seenOffers: ISeenOffersStore;
    semanticSearchAuth: ISemanticSearchAuthStore;
    subscribedRealEstates: ISubscribeRealEstateStore;
    tinderMultiLead: ITinderMultiLeadState;
}

export interface IRPStore extends IToolkitStore {
    // rest - please keep alphabetical order
    abTesting: IABTestingStore;
    article: IArticleStore;
    backToUrl: IBackToUrlStore;
    faq: IFaqStore;
    favourite: IFavouriteStore;
    propertyConfigurator: IPropertyConfiguratorStore;
    footer: IFooterStore;
    globals: IGlobalsStore;
    googleTagManager: IGoogleTagManagerStore;
    homepage: IHomepageStore;
    investmentOffer: IInvestmentOffersStore;
    leadForm: ILeadFormState;
    maps: IMapsStore;
    marketplace: IMarketplaceStore;
    metaData: IMetaDataStore;
    newsletter: INewsletterStore;
    notificationsSubscriptionModal: INotificationsSubscriptionModalStore;
    offerDetail: IOfferDetailStore;
    offerList: IOfferListStore;
    smartAds: ISmartAdsStore;
    offersNearby: IOffersNearbyStore;
    ourOffer: IOurOfferStore;
    offerListMap: IOfferListMapStore;
    partnersList: IPartnersListStore;
    promotionList: IPromotionListListStore;
    property: IPropertyDetailStore;
    report: IReportStore;
    responseState: IResponseStateStore;
    search: ISearchStore;
    sentLead: ISentLeadState;
    siteMap: ISiteMapStore;
    ui: IUiStore;
    user: IUserStore;
    variables: IVariablesStore;
    vendor: IVendorStore;
    viewType: IViewTypeStore;
    credit: IArticleListStore;
}

// Note: All keys from IRPStore must have corresponding reducers in rpReducerObj
// Additional reducers (like forYouModal, leadModal, offerListing) are allowed
export const rpReducerObj = {
    // Please keep alphabetical order
    abTesting: abTestingReducer,
    article: articleReducer,
    backToUrl: backToUrlReducer,
    faq: faqReducer,
    favourite: favouriteReducer,
    footer: footerReducer,
    forYouModal: forYouModalSlice.reducer,
    globals: globalsReducer,
    googleTagManager: googleTagManagerReducer,
    homepage: homepageReducer,
    investmentOffer: investmentOffersReducer,
    leadForm: leadFormReducer,
    mainLead: mainLeadSlice.reducer,
    maps: mapsReducer,
    marketplace: marketplaceReducer,
    metaData: metaDataReducer,
    newsletter: newsletterReducer,
    newsletterSubscriptionModal: newsletterSubscriptionModalSlice.reducer,
    navigation: navigationSlice.reducer,
    notificationsSubscriptionModal: notificationsSubscriptionModalReducer,
    offerDetail: offerDetailReducer,
    offerList: offerListReducer,
    offerListing: offerListingSlice.reducer,
    offerListMap: offerListMapReducer,
    offersNearby: offersNearbyReducer,
    partnersList: partnersListReducer,
    ourOffer: ourOfferReducer,
    pendingSinglePoi: pendingSinglePoiSlice.reducer,
    pricesUnlocked: pricesUnlockedSlice.reducer,
    promotionList: promotionListListReducer,
    property: propertyDetailReducer,
    propertyConfigurator: propertyConfiguratorStore,
    report: reportReducer,
    renderVariants: renderVariantsSlice.reducer,
    responseState: responseStateReducer,
    search: searchReducer,
    seenOffers: seenOffersSlice.reducer,
    semanticSearchAuth: semanticSearchAuthSlice.reducer,
    sentLead: sentLeadReducer,
    siteMap: siteMapReducer,
    smartAds: smartAdsReducer,
    subscribedRealEstates: subscribedRealEstatesSlice.reducer,
    tinderMultiLead: tinderMultiLeadSlice.reducer,
    ui: uiReducer,
    user: userReducer,
    variables: variablesReducer,
    vendor: vendorReducer,
    viewType: viewTypeReducer,
    credit: creditCalculatorReducer
};
import {abTestingReducer, IABTestingStore} from "@pg-mono/ab-testing";
import {IFavouriteStore} from "@pg-mono/favourites";
import {IResponseStateStore, responseStateReducer} from "@pg-mono/response-state";

import {ILeadFormState, leadFormReducer} from "../application/reducers/lead_form_reducer";
import {ISentLeadState, sentLeadReducer} from "../application/reducers/sent_lead_reducer";
import {mainLeadSlice} from "../application/state/main_lead_slice";
import {tinderMultiLeadSlice} from "../application/state/tinder_multi_lead_slice";
import {MainLeadState} from "../application/types/lead/MainLeadState";
import {ITinderMultiLeadState} from "../application/types/tinder_multi_lead/ITinderMultiLeadState";
import {IMarketplaceStore, marketplaceReducer} from "../architect_offers/reducers/marketplace_reducer";
import {articleReducer, IArticleStore} from "../article/reducers/article_reducer";
import {creditCalculatorReducer, IArticleListStore} from "../credit_calculator/reducers/credit_calculator_reducer";
import {faqReducer, IFaqStore} from "../faq/reducers/faq_reducer";
import {favouriteReducer} from "../favorite/reducers/favourite_reducer";
import {forYouModalSlice} from "../for_you/state/for_you_modal_slice";
import {IForYouModal} from "../for_you/types/IForYouModal";
import {homepageReducer, IHomepageStore} from "../homepage/reducers/homepage_reducer";
import {IInvestmentOffersStore, investmentOffersReducer} from "../investment_offer/reducers/investment_offer_reducer";
import {IMapsStore, mapsReducer} from "../maps/reducers/maps_reducer";
import {IPendingSinglePoiState, pendingSinglePoiSlice} from "../maps/state/pending_single_poi_slice";
import {IMetaDataStore, metaDataReducer} from "../meta_data/reducers/meta_data_reducer";
import {backToUrlReducer, IBackToUrlStore} from "../navigation/reducers/back_to_url_reducer";
import {footerReducer, IFooterStore} from "../navigation/reducers/footer_reducer";
import {INavigationStore, navigationSlice} from "../navigation/state/navigation_slice";
import {INewsletterStore, newsletterReducer} from "../newsletter/reducers/newsletter_reducer";
import {INewsletterSubscriptionModalStore, newsletterSubscriptionModalSlice} from "../newsletter/slice/newsletter_subscription_modal_slice";
import {IOfferDetailStore, offerDetailReducer} from "../offer/detail/reducers/offer_detail_reducer";
import {IOffersNearbyStore, offersNearbyReducer} from "../offer/detail/reducers/offers_nearby_reducer";
import {INotificationsSubscriptionModalStore, notificationsSubscriptionModalReducer} from "../offer/list/reducers/notifications_subscription_modal_reducer";
import {IOfferListStore, offerListReducer} from "../offer/list/reducers/offer_list_reducer";
import {IOfferListMapStore, offerListMapReducer} from "../offer/map/reducers/map_reducer";
import {IOfferListingStore, offerListingSlice} from "../offer/state/offer_listing_slice";
import {ISeenOffersStore, seenOffersSlice} from "../offer/state/seen_offers_slice";
import {IOurOfferStore, ourOfferReducer} from "../our_offer/reducers/our_offer_reducer";
import {IPartnersListStore, partnersListReducer} from "../partners/reducers/partners_list_reducer";
import {IPricesUnlockedState, pricesUnlockedSlice} from "../prices/state/prices_unlocked_slice";
import {IPromotionListListStore, promotionListListReducer} from "../promotion/reducers/promotion_list_reducer";
import {IPropertyDetailStore, propertyDetailReducer} from "../property/reducers/property_reducer";
import {IPropertyConfiguratorStore, propertyConfiguratorStore} from "../property_configurator/reducers/property_configurator_reducer";
import {ISubscribeRealEstateStore, subscribedRealEstatesSlice} from "../real_estate/state/subscribe_real_estate_modal_slice";
import {IReportStore, reportReducer} from "../report/price/reducers/report_reducer";
import {ISearchStore, searchReducer} from "../search/reducers/search_reducer";
import {ISemanticSearchAuthStore, semanticSearchAuthSlice} from "../semantic_search/state/semantic_search_auth_slice";
import {ISiteMapStore, siteMapReducer} from "../site_map/reducers/site_map_reducer";
import {ISmartAdsStore, smartAdsReducer} from "../smart_ad_server/reducer/smart_ads_reducer";
import {googleTagManagerReducer, IGoogleTagManagerStore} from "../tracking/google_tag_manager/reducers/google_tag_manager_reducer";
import {IUiStore, uiReducer} from "../ui/reducers/ui_reducer";
import {IUserStore, userReducer} from "../user/reducers/user_reducer";
import {IVariablesStore, variablesReducer} from "../variables/reducers/variables_reducer";
import {IVendorStore, vendorReducer} from "../vendor/reducers/vendor_reducer";
import {IViewTypeStore, viewTypeReducer} from "../view_type/view_type_reducer";
import {globalsReducer, IGlobalsStore} from "./reducers/globals_reducer";
import {renderVariantsSlice, RenderVariantsState} from "./render_variants/render_variants_slice";

// IMPORTANT! For new slices that use Redux Toolkit, always add them to rootReducerConfig!
// The goal is to eventually eliminate the separate state type and rely only on the type inferred from the reducer configuration!
interface IToolkitStore {
    // Reducers from Redux Toolkit slices - please keep alphabetical order
    forYouModal: IForYouModal;
    mainLead: MainLeadState;
    navigation: INavigationStore;
    newsletterSubscriptionModal: INewsletterSubscriptionModalStore;
    offerListing: IOfferListingStore;
    pendingSinglePoi: IPendingSinglePoiState;
    pricesUnlocked: IPricesUnlockedState;
    renderVariants: RenderVariantsState;
    seenOffers: ISeenOffersStore;
    semanticSearchAuth: ISemanticSearchAuthStore;
    subscribedRealEstates: ISubscribeRealEstateStore;
    tinderMultiLead: ITinderMultiLeadState;
}

export interface IRPStore extends IToolkitStore {
    // rest - please keep alphabetical order
    abTesting: IABTestingStore;
    article: IArticleStore;
    backToUrl: IBackToUrlStore;
    faq: IFaqStore;
    favourite: IFavouriteStore;
    propertyConfigurator: IPropertyConfiguratorStore;
    footer: IFooterStore;
    globals: IGlobalsStore;
    googleTagManager: IGoogleTagManagerStore;
    homepage: IHomepageStore;
    investmentOffer: IInvestmentOffersStore;
    leadForm: ILeadFormState;
    maps: IMapsStore;
    marketplace: IMarketplaceStore;
    metaData: IMetaDataStore;
    newsletter: INewsletterStore;
    notificationsSubscriptionModal: INotificationsSubscriptionModalStore;
    offerDetail: IOfferDetailStore;
    offerList: IOfferListStore;
    smartAds: ISmartAdsStore;
    offersNearby: IOffersNearbyStore;
    ourOffer: IOurOfferStore;
    offerListMap: IOfferListMapStore;
    partnersList: IPartnersListStore;
    promotionList: IPromotionListListStore;
    property: IPropertyDetailStore;
    report: IReportStore;
    responseState: IResponseStateStore;
    search: ISearchStore;
    sentLead: ISentLeadState;
    siteMap: ISiteMapStore;
    ui: IUiStore;
    user: IUserStore;
    variables: IVariablesStore;
    vendor: IVendorStore;
    viewType: IViewTypeStore;
    credit: IArticleListStore;
}

// Note: All keys from IRPStore must have corresponding reducers in rpReducerObj
// Additional reducers (like forYouModal, leadModal, offerListing) are allowed
export const rpReducerObj = {
    // Please keep alphabetical order
    abTesting: abTestingReducer,
    article: articleReducer,
    backToUrl: backToUrlReducer,
    faq: faqReducer,
    favourite: favouriteReducer,
    footer: footerReducer,
    forYouModal: forYouModalSlice.reducer,
    globals: globalsReducer,
    googleTagManager: googleTagManagerReducer,
    homepage: homepageReducer,
    investmentOffer: investmentOffersReducer,
    leadForm: leadFormReducer,
    mainLead: mainLeadSlice.reducer,
    maps: mapsReducer,
    marketplace: marketplaceReducer,
    metaData: metaDataReducer,
    newsletter: newsletterReducer,
    newsletterSubscriptionModal: newsletterSubscriptionModalSlice.reducer,
    navigation: navigationSlice.reducer,
    notificationsSubscriptionModal: notificationsSubscriptionModalReducer,
    offerDetail: offerDetailReducer,
    offerList: offerListReducer,
    offerListing: offerListingSlice.reducer,
    offerListMap: offerListMapReducer,
    offersNearby: offersNearbyReducer,
    partnersList: partnersListReducer,
    ourOffer: ourOfferReducer,
    pendingSinglePoi: pendingSinglePoiSlice.reducer,
    pricesUnlocked: pricesUnlockedSlice.reducer,
    promotionList: promotionListListReducer,
    property: propertyDetailReducer,
    propertyConfigurator: propertyConfiguratorStore,
    report: reportReducer,
    renderVariants: renderVariantsSlice.reducer,
    responseState: responseStateReducer,
    search: searchReducer,
    seenOffers: seenOffersSlice.reducer,
    semanticSearchAuth: semanticSearchAuthSlice.reducer,
    sentLead: sentLeadReducer,
    siteMap: siteMapReducer,
    smartAds: smartAdsReducer,
    subscribedRealEstates: subscribedRealEstatesSlice.reducer,
    tinderMultiLead: tinderMultiLeadSlice.reducer,
    ui: uiReducer,
    user: userReducer,
    variables: variablesReducer,
    vendor: vendorReducer,
    viewType: viewTypeReducer,
    credit: creditCalculatorReducer
};
